import classNames from "classnames";
import { compareAsc } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowPathIcon } from "@heroicons/react/24/solid";
import {
  ArrowTopRightOnSquareIcon,
  NewspaperIcon
} from "@heroicons/react/24/outline";

import Button from "../../components/atomic/Button";
import { CreditPolicyInterface, Rule } from "./interface";
import { notify } from "../../shared/context/AlertContext";
import GetAuthContext from "../../../../src/shared/context/AuthContext";
import SectionHeading from "../../components/atomic/SectionHeading";
import { LoanApplicationService } from "../../services/LoanApplication";
import PrivateComponent from "../../components/composed/PrivateComponent";
import { getFormattedDate, objectKeys } from "../../shared/utils/DateUtil";
import { roundToDecimal, getFormattedNumber } from "../../shared/utils/Global";
import TableWithPagination from "../../components/composed/TableWithPagination";
import {
  MultiSelectDropdown,
  IMultiSelectDropdownOption
} from "../../components/atomic/Dropdown";
import DetailsSection, {
  Item
} from "../../components/composed/DetailsSection/DetailsSection";
import DialogOne from "../../components/atomic/Dialog/DialogOne";

import { ruleColumns } from "../../shared/utils/constants";

function getFormattedCreditPolicies(
  creditPolicies: CreditPolicyInterface[]
): Array<IMultiSelectDropdownOption> {
  const result = new Map();
  creditPolicies?.forEach((creditPolicy) => {
    if (result.has(creditPolicy.ruleVersion)) {
      const policy = result.get(creditPolicy.ruleVersion);
      if (
        compareAsc(
          new Date(policy.decisionDate),
          new Date(creditPolicy.decisionDate)
        ) === -1
      ) {
        result.set(creditPolicy.ruleVersion, creditPolicy);
      }
    } else {
      result.set(creditPolicy.ruleVersion, creditPolicy);
    }
  });

  return Array.from(result, ([, value]) => ({
    name: value.ruleVersion,
    evaluationID: value?.evaluationID
  }));
}

function getOutputVariablesRow(key: string, value: any): Item[] {
  const result: Item[] = [];

  switch (true) {
    case typeof value === "number": {
      result.push({
        name: key,
        value: getFormattedNumber(roundToDecimal(value) ?? "")
      });
      break;
    }
    case typeof value === "string":
    case typeof value === "boolean": {
      result.push({
        name: key,
        value: value?.toString()
      });
      break;
    }
    case Array.isArray(value): {
      value.forEach((v: Record<string, number | string>, index: number) => {
        Object.keys(v).forEach((k) => {
          result.push({
            name: `${key}_${index}_${k}`,
            value: getFormattedNumber(roundToDecimal(v[k]) ?? "")
          });
        });
      });
      break;
    }
    default: {
      break;
    }
  }

  return result;
}

function getFormattedOutputVariables(outputVariables: {
  [key: string]: string;
}): Item[] {
  const formattedResult: Item[] = [];
  Object.keys(outputVariables).forEach((key) => {
    const data: Item[] = getOutputVariablesRow(key, outputVariables[key]);
    data.forEach((d) => {
      formattedResult.push(d);
    });
  });

  return formattedResult;
}

async function reRunPolicy(
  loanApplicationID: string,
  fetchToken: () => string,
  pageName: string
) {
  const response = await LoanApplicationService.reRunLastPolicy({
    token: fetchToken(),
    pageName,
    loanApplicationID
  });
  if (response.status) {
    notify({
      title: "Success",
      type: "success",
      text: "Policy re run successfully"
    });
  } else {
    notify({
      title: "Failure",
      type: "error",
      text: "There was an error in re running the policy"
    });
  }
}

function getRows(rules: Rule[]) {
  return rules.map((rule) => ({
    ...rule,
    decision: (
      <div
        className={classNames(
          "py-2 px-4  flex items-center justify-center rounded-md max-w-fit",
          rule.decision === "PASS"
            ? "bg-green-200 text-green-500"
            : rule.decision === "FAIL"
              ? "bg-red-200 text-red-500"
              : "bg-gray-200 text-black"
        )}
      >
        {rule.decision}
      </div>
    )
  }));
}

export default function CreditPolicy({
  pageName,
  userID,
  creditPolicies = []
}: {
  userID: string;
  pageName?: string;
  creditPolicies?: CreditPolicyInterface[];
}) {
  const { loanID, userID: leadUserID } = useParams();
  const loanApplicationID = (loanID || leadUserID) as string;
  const { fetchToken, getPageConfig } = GetAuthContext();
  const pageConfig = getPageConfig();
  const [showSentinelPolicyIframe, setShowSentinelPolicyIframe] =
    useState(false);
  const [iframeUrl, setIframeUrl] = useState("");

  const [formattedCreditPolicies, setFormattedCreditPolicies] = useState<
    IMultiSelectDropdownOption[]
  >(getFormattedCreditPolicies(creditPolicies));

  useEffect(() => {
    const policies = getFormattedCreditPolicies(creditPolicies);
    setFormattedCreditPolicies(policies);
    setSelected(policies[0]);
  }, [creditPolicies]);

  const [selected, setSelected] = useState<IMultiSelectDropdownOption>(
    formattedCreditPolicies?.[0]
  );
  const [selectedCreditPolicy, setCreditPolicy] =
    useState<CreditPolicyInterface>({
      ruleVersion: "",
      rules: [],
      outputVariables: {},
      decisionDate: "1980-01-01",
      isSimulated: false
    });

  useEffect(() => {
    if (creditPolicies && selected) {
      const filteredPolicies = creditPolicies.reduce<CreditPolicyInterface[]>(
        (acc, policy) => {
          if (policy.ruleVersion === selected.name) {
            const existingPolicy = acc.find(
              (p) => p.ruleVersion === policy.ruleVersion
            );
            if (
              !existingPolicy ||
              new Date(policy.decisionDate) >
                new Date(existingPolicy.decisionDate)
            ) {
              return [policy];
            }
          }
          return acc;
        },
        []
      );

      if (filteredPolicies[0]) {
        setCreditPolicy(filteredPolicies[0]);
      }
    }
  }, [selected, creditPolicies]);

  const sentinelPolicyLink = async () => {
    const payload = {
      userID,
      pageName,
      evaluationID: selected?.evaluationID,
      loanApplicationID
    };
    const response = (await LoanApplicationService.sentinelPolicyLink({
      token: fetchToken(),
      pageName,
      payload
    })) as any;
    if (response?.status && response?.data?.url) {
      setShowSentinelPolicyIframe(true);
      setIframeUrl(response?.data?.url);
    } else {
      notify({
        title: "Error",
        text: response?.error
      });
    }
  };

  const sections = useMemo(() => {
    const builtSections: Record<string, JSX.Element | false> = {
      output_variables: selectedCreditPolicy?.outputVariables && (
        <DetailsSection
          title="Output variables"
          items={getFormattedOutputVariables(
            selectedCreditPolicy.outputVariables
          )}
          style={{ titlePadding: false }}
        />
      ),
      rules: selectedCreditPolicy?.rules?.length > 0 && (
        <>
          <SectionHeading heading="Rules" />
          <TableWithPagination
            wrap={false}
            columns={ruleColumns}
            rows={getRows(selectedCreditPolicy.rules)}
            pagination={{
              enabled: false,
              total: 0,
              page: 0,
              limit: 0,
              setPage: () => {},
              styles: { mobile: "", desktop: "lg:flex-none" }
            }}
            emptyText="No Data"
          />
        </>
      )
    };
    const keys = objectKeys(builtSections);
    if (pageConfig?.[pageName!]?.["ordering"]) {
      keys.sort(
        (section1, section2) =>
          pageConfig[pageName!]["ordering"][section1] -
          pageConfig[pageName!]["ordering"][section2]
      );
    }
    return keys
      .map((section) => builtSections[section])
      .filter(Boolean) as JSX.Element[];
  }, [selectedCreditPolicy, pageConfig, pageName]);

  return (
    <div>
      {creditPolicies !== null && creditPolicies.length > 0 ? (
        <>
          <div className="my-6 mx-3">
            <div className="flex w-full items-center my-6">
              <MultiSelectDropdown
                label="Policies"
                selected={selected}
                setSelected={setSelected}
                options={formattedCreditPolicies}
              />

              <div className="ml-4" />

              <PrivateComponent
                requiredPage={pageName!}
                requiredFunctions={["Rerun Last Policy"]}
              >
                <Button
                  color="primary"
                  className="flex items-center text-xs"
                  onClick={() =>
                    reRunPolicy(loanApplicationID, fetchToken, pageName!)
                  }
                >
                  <ArrowPathIcon className="h-4 w-4 text-inherit mr-2" />
                  Re-run last policy
                </Button>
              </PrivateComponent>

              <PrivateComponent
                requiredPage={pageName!}
                requiredFunctions={["Sentinel Policy Link Redirect"]}
              >
                <Button
                  color="primary"
                  onClick={sentinelPolicyLink}
                  className="flex items-center text-xs ml-3"
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4 text-inherit mr-2" />
                  View policy on Sentinel
                </Button>
              </PrivateComponent>
            </div>

            <div className="my-4">
              <span className="text-xs">
                Rule Version: {selectedCreditPolicy?.ruleVersion}
              </span>
              <br />

              <span className="text-xs">
                Decision Date:{" "}
                {getFormattedDate(selectedCreditPolicy.decisionDate, true)}
              </span>
            </div>

            {sections.map((Section, index) => (
              <div key={index} className="mb-4">
                {Section}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="my-3 mx-3">
          <div className="flex items-baseline flex-col">
            <SectionHeading heading="Credit Policy" />
            <div className="w-full">
              <div className="flex flex-col items-center mt-10">
                <div className="h-32 w-32 rounded-full bg-indigo-50 flex items-center justify-center">
                  <NewspaperIcon className="h-14 w-14 text-gray-500" />
                </div>
                <div className="text-sm mt-2 font-medium ">
                  No credit policies available
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <DialogOne
        open={showSentinelPolicyIframe}
        setOpen={setShowSentinelPolicyIframe}
        styles={{
          panel: "sm:max-w-6xl md:max-w-full h-[calc(100vh-200px)]"
        }}
        title="Sentinel Policy"
      >
        <div className="w-full h-full overflow-auto">
          <iframe className="w-full h-600" src={iframeUrl} />
        </div>
      </DialogOne>
    </div>
  );
}
